import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { SERVICES, PREMIUM_SERVICES } from "../../data/site";

export default function Services() {
  return (
    <section
      id="services"
      className="py-20 md:py-28 bg-white"
      data-testid="services-section"
    >
      <div className="container">
        <div className="grid lg:grid-cols-12 gap-6 items-end mb-12">
          <div className="lg:col-span-7">
            <p className="text-overline text-electric mb-3">01 — Services</p>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold text-charcoal text-balance">
              Full-Stack App Marketing Services
            </h2>
          </div>
          <p className="lg:col-span-5 text-zinc-600 text-lg leading-relaxed">
            Seven tightly-coupled services — from ASO and user acquisition to
            influencer campaigns, analytics and advanced paid marketing.
          </p>
        </div>

        {/* Main service grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
          {SERVICES.map((s, idx) => {
            const Icon = s.icon;
            const dark = s.accent;
            return (
              <motion.article
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: idx * 0.05 }}
                className={`group relative ${s.span ?? ""}`}
                data-testid={`service-card-${s.slug}`}
              >
                <Link
                  to={s.href}
                  className={`flex flex-col h-full p-7 md:p-9 ${
                    dark ? "bg-charcoal text-white" : "bg-white text-charcoal"
                  } hover:bg-electric hover:text-white transition-colors duration-300`}
                >
                  <div className="flex items-start justify-between">
                    <div
                      className={`w-12 h-12 grid place-items-center border ${
                        dark
                          ? "border-white/20 bg-white/10"
                          : "border-charcoal/15 bg-zinc-50"
                      } group-hover:bg-white group-hover:border-white`}
                    >
                      <Icon
                        className="w-5 h-5 text-electric group-hover:text-electric"
                        strokeWidth={2}
                      />
                    </div>
                    <ArrowUpRight
                      className={`w-5 h-5 ${
                        dark ? "text-white/70" : "text-zinc-400"
                      } group-hover:text-white transition-transform group-hover:translate-x-1 group-hover:-translate-y-1`}
                    />
                  </div>
                  <h3 className="font-display text-2xl md:text-[1.7rem] font-bold mt-8 leading-tight">
                    {s.title}
                  </h3>
                  <p
                    className={`mt-3 text-[15px] leading-relaxed ${
                      dark ? "text-zinc-300" : "text-zinc-600"
                    } group-hover:text-white/90`}
                  >
                    {s.description}
                  </p>
                  <ul className="mt-6 flex flex-wrap gap-2">
                    {s.bullets.map((b) => (
                      <li
                        key={b}
                        className={`text-xs px-2.5 py-1 border ${
                          dark
                            ? "border-white/20 text-zinc-200"
                            : "border-zinc-300 text-zinc-700"
                        } group-hover:border-white/60 group-hover:text-white`}
                      >
                        {b}
                      </li>
                    ))}
                  </ul>
                </Link>
              </motion.article>
            );
          })}
        </div>

        {/* Premium Services strip */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="relative mt-px overflow-hidden"
          data-testid="premium-services-strip"
        >
          {/* Animated shimmer border */}
          <motion.div
            className="absolute inset-0 rounded-none pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, transparent 0%, #2196F3 25%, #a855f7 50%, #2196F3 75%, transparent 100%)",
              backgroundSize: "200% 100%",
            }}
            animate={{ backgroundPosition: ["200% 0%", "-200% 0%"] }}
            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          />

          {/* Inner content — sits 2px inside to reveal the shimmer border */}
          <div className="relative m-[2px] bg-gradient-to-r from-charcoal via-zinc-800 to-charcoal px-7 md:px-9 py-7">
            {/* Subtle moving glow behind tags */}
            <motion.div
              className="absolute inset-0 opacity-20 pointer-events-none"
              style={{
                background:
                  "radial-gradient(ellipse 40% 80% at 0% 50%, #2196F3, transparent)",
              }}
              animate={{ x: ["0%", "100%", "0%"] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            />

            <div className="relative flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-8">
              {/* Badge */}
              <div className="flex-shrink-0">
                <motion.span
                  className="inline-flex items-center gap-2 bg-electric text-white text-[10px] font-black uppercase tracking-widest px-3 py-1.5"
                  animate={{ boxShadow: ["0 0 0px #2196F3", "0 0 18px #2196F388", "0 0 0px #2196F3"] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                >
                  <motion.span
                    className="w-1.5 h-1.5 rounded-full bg-white"
                    animate={{ opacity: [1, 0.2, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  />
                  ✦ Premium Services
                </motion.span>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-3">
                {PREMIUM_SERVICES.map((ps, idx) => (
                  <motion.div
                    key={ps.title}
                    initial={{ opacity: 0, y: 8 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35, delay: 0.2 + idx * 0.07 }}
                    whileHover={{ y: -2, scale: 1.03 }}
                  >
                    <Link
                      to={ps.href}
                      className="group relative inline-flex items-center gap-1.5 border border-white/20 bg-white/5 text-white text-sm font-medium px-4 py-2 hover:bg-electric hover:border-electric transition-all duration-200"
                      data-testid={`premium-service-${ps.short.toLowerCase()}`}
                    >
                      {ps.title}
                      <ArrowUpRight className="w-3.5 h-3.5 text-electric group-hover:text-white transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
